(() => {
  if (window.HalfpipedBoardCartRemoval) return;

  let removalInProgress = false;
  let confirmationOpen = false;
  let confirmedSnapshot = null;
  const SNAPSHOT_TTL_MS = 30000;
  const bundleOf = item => String(item?.properties?._hp_board_bundle_id || '');
  const endpoint = path => `${window.Shopify?.routes?.root || '/'}${path}`;
  const registry = () => {
    const mutations = window.HalfpipedCartMutations;
    if (!mutations?.begin || !mutations?.whenIdle || !mutations?.getRevision) {
      throw new Error('Please reload the page before removing this board.');
    }
    return mutations;
  };
  const requestCart = async (path, options = {}) => {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 12000);
    try {
      const response = await window.fetch(endpoint(path), {
        credentials: 'same-origin', cache: 'no-store', ...options,
        headers: { Accept: 'application/json', ...options.headers }, signal: controller.signal,
      });
      const cart = await response.json();
      if (!response.ok || cart.errors) {
        throw new Error(cart.description || cart.message || String(cart.errors || 'Could not remove the board. Please try again.'));
      }
      if (!Array.isArray(cart.items)) throw new Error('Could not verify the updated cart. Please review your cart.');
      return cart;
    } catch (error) {
      if (controller.signal.aborted) throw new Error('The cart took too long to respond. Please review your cart before trying again.');
      throw error;
    } finally { clearTimeout(timer); }
  };

  const invalidateCart = () => { confirmedSnapshot = null; };
  const rememberCart = (cart, stamp) => {
    const mutations = window.HalfpipedCartMutations;
    if (stamp?.confirmed !== true || !Number.isSafeInteger(stamp.cartRevision)
      || stamp.cartRevision !== mutations?.getRevision?.() || !Array.isArray(cart?.items)) return;
    // Rendering code can adjust its cart object. Preserve the verified keys
    // separately so an optimistic render cannot alter the removal snapshot.
    const copy = JSON.parse(JSON.stringify(cart));
    delete copy.sections;
    confirmedSnapshot = { cart: copy, revision: stamp.cartRevision, timestamp: Date.now() };
  };
  const currentSnapshot = (key, bundleId, revision) => {
    const snapshot = confirmedSnapshot;
    if (!snapshot || snapshot.revision !== revision || Date.now() - snapshot.timestamp >= SNAPSHOT_TTL_MS
      || !snapshot.cart.items.some(item => item.key === key && bundleOf(item) === bundleId)) return null;
    return JSON.parse(JSON.stringify(snapshot.cart));
  };
  document.addEventListener('cart:update', event => {
    const data = event.detail?.data;
    if (data?.cartConfirmed === true) {
      rememberCart(event.detail?.resource, { confirmed: true, cartRevision: data.cartRevision });
    }
  });
  window.addEventListener('pagehide', invalidateCart);
  window.addEventListener('pageshow', event => { if (event.persisted) invalidateCart(); });
  document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'hidden') invalidateCart(); });

  const confirmRemoval = () => {
    confirmationOpen = true;
    try {
      return window.confirm('Hey there! Removing this item will also remove the rest of this custom complete bundle. Unrelated items and other bundles will stay in your cart.\n\nDo you want to continue?');
    } finally { confirmationOpen = false; }
  };

  const remove = options => {
    const { key, cart, bundleId: suppliedBundleId, cancelPending, onPending, onCart, sections = [], sectionsUrl } = options;
    const selected = cart?.items?.find(item => item.key === key);
    const bundleId = selected ? bundleOf(selected) : String(suppliedBundleId || '');
    if (!bundleId) return Promise.resolve({ handled: false, cancelled: false, cart });
    if (removalInProgress) return Promise.resolve({ handled: true, cancelled: true, cart });

    const operation = async () => {
      const resume = [];
      document.dispatchEvent(new CustomEvent('hp:board-removal:confirming', { detail: { bundleId, resume } }));
      if (!confirmRemoval()) {
        for (const restore of resume) restore();
        return { handled: true, cancelled: true, cart };
      }
      const mutations = registry();
      cancelPending?.({ bundleId, keys: (cart?.items || []).filter(item => bundleOf(item) === bundleId).map(item => item.key) });
      for (let attempt = 0; attempt < 3; attempt += 1) {
        // Wait before registering our own work so we cannot wait on ourselves.
        await mutations.whenIdle();
        if (mutations.isPending?.()) continue;
        const cachedCart = currentSnapshot(key, bundleId, mutations.getRevision());
        const finish = mutations.begin();
        const revision = mutations.getRevision();
        try {
          const freshCart = cachedCart || await requestCart('cart.js');
          if (revision !== mutations.getRevision()) { finish(); continue; }
          const keys = freshCart.items.filter(item => bundleOf(item) === bundleId).map(item => item.key);
          if (!keys.length) {
            const stamp = { cartRevision: revision, confirmed: true };
            rememberCart(freshCart, stamp);
            await onCart?.(freshCart, stamp);
            return { handled: true, cancelled: false, cart: freshCart };
          }
          cancelPending?.({ bundleId, keys });
          // This callback is synchronous: no writer may start between the
          // revision check and registration of the batch update request.
          onPending?.({ cart: freshCart, keys, bundleId });
          if (revision !== mutations.getRevision()) { finish(); continue; }
          const payload = { updates: Object.fromEntries(keys.map(lineKey => [lineKey, 0])) };
          if (sections.length) {
            payload.sections = [...new Set(sections)].slice(0, 5).join(',');
            payload.sections_url = sectionsUrl || window.location.pathname;
          }
          const update = requestCart('cart/update.js', {
            method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload),
          });
          const cartRevision = mutations.getRevision();
          const wasExclusive = mutations.getPendingCount?.() <= 2;
          const updatedCart = await update;
          const stamp = { cartRevision, confirmed: wasExclusive && cartRevision === mutations.getRevision() };
          rememberCart(updatedCart, stamp);
          await onCart?.(updatedCart, stamp);
          return { handled: true, cancelled: false, cart: updatedCart };
        } catch (error) { finish(error); throw error; }
        finally { finish(); }
      }
      throw new Error('Your cart is still changing. Please wait and try removing the board again.');
    };
    removalInProgress = true;
    return operation().finally(() => { removalInProgress = false; });
  };

  window.HalfpipedBoardCartRemoval = { remove, rememberCart, invalidateCart, isOpen: () => confirmationOpen };
})();
